/** 编辑器主界面：桌面三栏 / 手机上下+切换。 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CutShape, FoldId } from '../domain/types';
import { FOLD_LIST, describeSizeKind, getFold } from '../domain/folds/definitions';
import { diameterFromSquareSide, maxFinishedSize } from '../domain/folds/geometry';
import { PAPER_COLORS, parseProject, serializeProject } from '../domain/project/schema';
import { analyze } from '../domain/manufacturability/analyze';
import { PRESETS, type Preset } from '../content/presets';
import { Canvas } from './Canvas';
import { UnfoldedScene } from './Paint';
import { FoldDiagram } from './FoldDiagram';
import { useSceneViewBox } from './Paint';
import { useEditor, type Tool } from './state';
import { buildProjectPdf } from '../export/pdf';
import { createCanvasTextRenderer } from '../export/text.browser';
import { projectToSvg } from '../export/svg';
import { DPI_PRESETS, downloadBlob, downloadText, dpiToPxPerMm, projectToPngBlob } from '../export/png';
import '../styles/app.css';

const TOOLS: Array<{ id: Tool; label: string; hint: string }> = [
  { id: 'select', label: '选择', hint: '点选剪口后可拖动移动；几何剪口可拖动圆点缩放' },
  { id: 'polygon', label: '多边形', hint: '逐点点击，点回第一个点或按「完成」闭合' },
  { id: 'freehand', label: '自由绘制', hint: '按住拖动画出任意区域，松开自动简化闭合' },
  { id: 'circle', label: '圆形', hint: '从圆心拖出半径' },
  { id: 'triangle', label: '三角形', hint: '从中心拖出大小与方向' },
  { id: 'rect', label: '矩形', hint: '从中心拖出宽高' },
];

export function App() {
  const { state, dispatch, notify, storage } = useEditor();
  const [mobileTab, setMobileTab] = useState<'edit' | 'preview'>('edit');
  const [liveCuts, setLiveCuts] = useState<CutShape[] | null>(null);
  const [pendingFold, setPendingFold] = useState<FoldId | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [dpi, setDpi] = useState<number>(300);
  const fileRef = useRef<HTMLInputElement>(null);

  const project = state.project;
  const fold = getFold(project.foldId);
  const max = useMemo(() => maxFinishedSize(fold), [fold]);
  const analysis = useMemo(
    () => analyze(project.foldId, project.finishedSize, project.cuts),
    [project.foldId, project.finishedSize, project.cuts],
  );
  const previewProject = useMemo(() => {
    const cuts = liveCuts ?? project.cuts;
    if (!state.draft || state.draft.length < 3) {
      return cuts === project.cuts ? project : { ...project, cuts };
    }
    const draftCut: CutShape = { id: '__draft-preview__', kind: 'polygon', points: state.draft };
    return { ...project, cuts: [...cuts, draftCut] };
  }, [liveCuts, project, state.draft]);
  const previewBox = useSceneViewBox(previewProject, 8);
  const previewIsLive = liveCuts !== null || Boolean(state.draft?.length);
  const previewIsEmpty = previewProject.cuts.length === 0;
  const selected = project.cuts.find((c) => c.id === state.selectedId) ?? null;

  // 支持从模板库/教程页带参数进入：/editor/?preset=xxx 或 /editor/?fold=xxx
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const presetId = params.get('preset');
    if (presetId) {
      const preset = PRESETS.find((p) => p.id === presetId);
      if (preset) {
        dispatch({ type: 'loadPreset', preset });
        return;
      }
    }
    const foldParam = params.get('fold');
    if (foldParam && FOLD_LIST.some((f) => f.id === foldParam)) {
      dispatch({ type: 'setFold', foldId: foldParam as FoldId, keepCuts: false });
    }
  }, []);
  useEffect(() => {
    if (!state.notice) return;
    const t = window.setTimeout(() => dispatch({ type: 'notice', notice: null }), state.notice.kind === 'error' ? 12000 : 4000);
    return () => window.clearTimeout(t);
  }, [state.notice, dispatch]);

  const onFoldPick = (id: FoldId) => {
    if (id === project.foldId) return;
    if (project.cuts.length > 0) {
      setPendingFold(id);
      return;
    }
    dispatch({ type: 'setFold', foldId: id, keepCuts: false });
  };

  const confirmFold = (keep: boolean) => {
    if (!pendingFold) return;
    dispatch({ type: 'setFold', foldId: pendingFold, keepCuts: keep });
    notify(keep ? 'info' : 'success', keep ? '已切换折法，原有剪口超出新边界的部分会被裁掉。' : '已切换折法并清空剪口。');
    setPendingFold(null);
  };

  const applyPreset = (preset: Preset) => {
    if (preset.foldId !== project.foldId) {
      dispatch({ type: 'loadPreset', preset });
      return;
    }
    dispatch({ type: 'loadPreset', preset });
  };

  const onExportSvg = () => {
    downloadText(projectToSvg(project), filename('svg'), 'image/svg+xml;charset=utf-8');
    notify('success', 'SVG 已导出（展开图案，可缩放）。');
  };

  const onExportPng = async () => {
    setBusy('正在生成 PNG…');
    try {
      const blob = await projectToPngBlob(project, { pxPerMm: dpiToPxPerMm(dpi) });
      downloadBlob(blob, filename('png'));
      notify('success', `透明 PNG 已导出（${dpi} dpi）。`);
    } catch (err) {
      notify('error', `PNG 导出失败：${(err as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const onExportPdf = async () => {
    setBusy('正在生成 A4 PDF…');
    try {
      const bytes = await buildProjectPdf(project, { textRenderer: createCanvasTextRenderer() });
      downloadBlob(new Blob([bytes as unknown as BlobPart], { type: 'application/pdf' }), filename('pdf'));
      notify('success', 'A4 PDF 已导出（含制作模板、步骤页、描剪底样，50 mm 校验条已印入）。');
    } catch (err) {
      notify('error', `PDF 导出失败：${(err as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const onExportJson = () => {
    downloadText(serializeProject(project), filename('json'), 'application/json;charset=utf-8');
    notify('success', '作品 JSON 已导出，可稍后导入恢复。');
  };

  const onImport = async (file: File) => {
    const text = await file.text();
    const result = parseProject(text);
    if (!result.ok || !result.project) {
      const first = result.issues.find((i) => i.level === 'error');
      notify('error', `导入失败：${first?.message ?? '文件格式不正确'}。当前作品未改动。`);
      return;
    }
    const warns = result.issues.filter((i) => i.level === 'warn');
    dispatch({ type: 'loadProject', project: result.project });
    if (warns.length) notify('warn', `已导入，但有 ${warns.length} 处提示：${warns[0].message}`);
  };

  const onClear = () => {
    if (!project.cuts.length) return;
    if (!window.confirm('清空所有剪口？可以用撤销恢复。')) return;
    dispatch({ type: 'clearCuts' });
    notify('info', '已清空，可用撤销恢复。');
  };

  const sizeValue = project.finishedSize.value;

  return (
    <div className="app">
      <header className="topbar">
        <a className="brand" href="/">
          <span className="brand-mark">剪</span>
          <span>窗花剪纸</span>
        </a>
        <nav className="topnav">
          <a href="/templates/">模板库</a>
          <a href="/folds/">折法教程</a>
          <a href="/print-guide/">打印说明</a>
          <a href="/">首页</a>
        </nav>
        <div className="topactions">
          <button type="button" onClick={() => dispatch({ type: 'undo' })} disabled={!state.past.length} title="撤销 (Ctrl+Z)">
            ↶ 撤销
          </button>
          <button type="button" onClick={() => dispatch({ type: 'redo' })} disabled={!state.future.length} title="重做 (Ctrl+Y)">
            ↷ 重做
          </button>
          <button type="button" className="danger" onClick={onClear} disabled={!project.cuts.length}>
            清空
          </button>
        </div>
      </header>

      <div className="mobile-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={mobileTab === 'edit'} className={mobileTab === 'edit' ? 'active' : ''} onClick={() => setMobileTab('edit')}>
          编辑
        </button>
        <button type="button" role="tab" aria-selected={mobileTab === 'preview'} className={mobileTab === 'preview' ? 'active' : ''} onClick={() => setMobileTab('preview')}>
          检查与导出
        </button>
      </div>

      <main className={`workspace tab-${mobileTab}`}>
        {/* ---------------- 左：设置 */}
        <aside className="panel panel-settings">
          <section>
            <h2>折法</h2>
            <div className="fold-grid">
              {FOLD_LIST.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`fold-card${f.id === project.foldId ? ' active' : ''}`}
                  onClick={() => onFoldPick(f.id)}
                  aria-pressed={f.id === project.foldId}
                >
                  <FoldDiagram foldId={f.id} size={project.finishedSize} className="fold-diagram" />
                  <span className="fold-name">{f.name}</span>
                  <span className="fold-meta">{f.layers} 层 · 难度 {f.difficulty}/5</span>
                </button>
              ))}
            </div>
            <p className="hint">
              当前：{fold.name} · {fold.layers} 层 · {describeSizeKind(project.finishedSize.kind)}
              {fold.needsAngleGuide && <strong className="warn-text"> · 需要角度辅助线</strong>}
            </p>
          </section>

          <section>
            <h2>成品尺寸</h2>
            {project.finishedSize.kind === 'diameter' ? (
              <label className="field">
                <span>
                  外接圆直径 <b>{sizeValue[0].toFixed(1)} mm</b>
                </span>
                <input
                  type="range"
                  min={40}
                  max={max.value[0]}
                  step={1}
                  value={sizeValue[0]}
                  onChange={(e) => dispatch({ type: 'setSize', value: [Number(e.target.value)] })}
                />
                <small>
                  正方形纸边长 {diameterFromSquareSide(sizeValue[0]).toFixed(1)} mm，最大 {max.value[0].toFixed(1)} mm
                </small>
              </label>
            ) : (
              <div className="field-row">
                <label className="field">
                  <span>
                    宽 <b>{sizeValue[0].toFixed(0)} mm</b>
                  </span>
                  <input
                    type="range"
                    min={40}
                    max={max.value[0]}
                    step={1}
                    value={sizeValue[0]}
                    onChange={(e) => dispatch({ type: 'setSize', value: [Number(e.target.value), sizeValue[1]] })}
                  />
                </label>
                <label className="field">
                  <span>
                    高 <b>{sizeValue[1].toFixed(0)} mm</b>
                  </span>
                  <input
                    type="range"
                    min={40}
                    max={max.value[1]}
                    step={1}
                    value={sizeValue[1]}
                    onChange={(e) => dispatch({ type: 'setSize', value: [sizeValue[0], Number(e.target.value)] })}
                  />
                </label>
              </div>
            )}
          </section>

          <section>
            <h2>纸张颜色</h2>
            <div className="swatches">
              {PAPER_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={c === project.paperColor ? 'swatch active' : 'swatch'}
                  style={{ background: c }}
                  aria-label={`纸张颜色 ${c}`}
                  onClick={() => dispatch({ type: 'setColor', color: c })}
                />
              ))}
            </div>
            <p className="hint">颜色只影响屏幕预览；打印为省墨黑白，不铺满底色。</p>
          </section>

          <section>
            <h2>辅助</h2>
            <label className="check">
              <input
                type="checkbox"
                checked={project.guides.showFoldLines}
                onChange={(e) => dispatch({ type: 'setGuides', patch: { showFoldLines: e.target.checked } })}
              />
              显示折线
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={project.guides.showGrid}
                onChange={(e) => dispatch({ type: 'setGuides', patch: { showGrid: e.target.checked } })}
              />
              显示网格
            </label>
            <label className="field">
              <span>吸附精度 {project.guides.snapMm} mm</span>
              <input
                type="range"
                min={0}
                max={5}
                step={0.5}
                value={project.guides.snapMm}
                onChange={(e) => dispatch({ type: 'setGuides', patch: { snapMm: Number(e.target.value) } })}
              />
            </label>
          </section>

          <section>
            <h2>原创模板</h2>
            <div className="preset-list">
              {PRESETS.filter((p) => p.foldId === project.foldId).map((p) => (
                <button key={p.id} type="button" className="preset-item" onClick={() => applyPreset(p)}>
                  <span className="preset-name">{p.name}</span>
                  <span className="preset-meta">
                    难度 {p.difficulty}/5 · {p.status === 'program-checked' ? '程序检查' : p.status === 'pending-physical' ? '待实物验证' : '已实物验证'}
                  </span>
                </button>
              ))}
              <p className="hint">当前折法下的模板；切换折法可看到其余模板。</p>
            </div>
            <a className="link" href="/templates/">
              查看全部 {PRESETS.length} 款模板 →
            </a>
          </section>
        </aside>

        {/* ---------------- 中：画布 */}
        <section className="stage">
          <div className="toolbar" role="toolbar" aria-label="剪口工具">
            {TOOLS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={state.tool === t.id ? 'tool active' : 'tool'}
                onClick={() => dispatch({ type: 'setTool', tool: t.id })}
                title={t.hint}
                aria-pressed={state.tool === t.id}
              >
                {t.label}
              </button>
            ))}
            <span className="toolbar-sep" />
            <button type="button" className="tool" disabled={!state.selectedId} onClick={() => state.selectedId && dispatch({ type: 'removeCut', id: state.selectedId })}>
              删除选中
            </button>
          </div>
          <p className="tool-hint">{TOOLS.find((t) => t.id === state.tool)?.hint}</p>
          <div className="design-grid">
            <section className="design-pane editor-pane" aria-labelledby="folded-canvas-title">
              <div className="design-pane-head">
                <div>
                  <span className="pane-kicker">在这里剪</span>
                  <h2 id="folded-canvas-title">折后纸张</h2>
                </div>
                <span className="fold-chip">{fold.name}</span>
              </div>
              <Canvas state={state} dispatch={dispatch} notify={notify} onPreviewCutsChange={setLiveCuts} />
            </section>

            <section className={`design-pane live-preview-pane${previewIsLive ? ' is-live' : ''}`} aria-labelledby="live-preview-title">
              <div className="design-pane-head">
                <div>
                  <span className="pane-kicker">同步展开</span>
                  <h2 id="live-preview-title">实时成品</h2>
                </div>
                <span className="live-indicator"><i />实时预览</span>
              </div>
              <div className="live-preview-canvas">
                <svg viewBox={previewBox} className="preview-svg live-preview-svg" preserveAspectRatio="xMidYMid meet" role="img" aria-label="实时展开后的成品预览">
                  <UnfoldedScene project={previewProject} />
                </svg>
                {previewIsEmpty && (
                  <div className="preview-empty">
                    <strong>画一个剪口试试</strong>
                    <span>左侧每一步都会立即展开到这里</span>
                  </div>
                )}
              </div>
              <p className="live-preview-status" aria-live="polite">
                {previewIsLive
                  ? '正在预览未完成的剪口，完成绘制后自动保存'
                  : project.cuts.length
                    ? `已同步 ${project.cuts.length} 个剪口`
                    : '等待第一个剪口'}
              </p>
            </section>
          </div>
          <div className="stage-footer">
            <span>{project.cuts.length} 个剪口</span>
            <span>{state.saving ? '正在保存…' : '已自动保存'}</span>
            <span>{storage.available ? '' : '本地保存不可用，请手动导出 JSON'}</span>
          </div>
        </section>

        {/* ---------------- 右：检查与导出 */}
        <aside className="panel panel-preview">
          <section>
            <h2>作品摘要</h2>
            <p className="summary-note">展开效果已固定在编辑画布旁，并会在绘制过程中实时同步。</p>
            <ul className="stats">
              <li>
                <span>折法</span>
                <b>
                  {fold.name}（{fold.layers} 层）
                </b>
              </li>
              <li>
                <span>{describeSizeKind(project.finishedSize.kind)}</span>
                <b>
                  {project.finishedSize.kind === 'diameter'
                    ? `${project.finishedSize.value[0].toFixed(1)} mm`
                    : `${project.finishedSize.value[0].toFixed(0)} × ${project.finishedSize.value[1].toFixed(0)} mm`}
                </b>
              </li>
              <li>
                <span>难度</span>
                <b>{fold.difficulty}/5</b>
              </li>
              <li>
                <span>已剪面积</span>
                <b>{(analysis.metrics.removedRatio * 100).toFixed(1)}%</b>
              </li>
            </ul>
          </section>

          <section>
            <h2>
              可制作性检查 <span className="badge">近似</span>
            </h2>
            <ul className="issues">
              {analysis.issues.length === 0 && <li className="ok">未发现明显问题。</li>}
              {analysis.issues.map((i) => (
                <li key={i.code} className={i.level}>
                  {i.message}
                </li>
              ))}
            </ul>
            <p className="hint">
              检测基于 {analysis.metrics.cellMm} mm 栅格采样，只作风险提示，不保证实物一定成功；最终以试剪为准。
            </p>
          </section>

          <section>
            <h2>导出</h2>
            <div className="export-buttons">
              <button type="button" className="primary" onClick={onExportPdf} disabled={busy !== null}>
                A4 PDF
              </button>
              <button type="button" onClick={onExportSvg} disabled={busy !== null}>
                SVG
              </button>
              <button type="button" onClick={onExportPng} disabled={busy !== null}>
                透明 PNG
              </button>
              <button type="button" onClick={onExportJson} disabled={busy !== null}>
                作品 JSON
              </button>
            </div>
            <label className="field">
              <span>PNG 分辨率</span>
              <select value={dpi} onChange={(e) => setDpi(Number(e.target.value))}>
                {DPI_PRESETS.map((d) => (
                  <option key={d.dpi} value={d.dpi}>
                    {d.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="import-row">
              <input
                ref={fileRef}
                type="file"
                accept="application/json,.json"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onImport(f).catch((err) => notify('error', `导入失败：${(err as Error).message}`));
                  e.target.value = '';
                }}
                hidden
              />
              <button type="button" className="ghost" onClick={() => fileRef.current?.click()}>
                导入作品 JSON
              </button>
            </div>
            {busy && <p className="hint">{busy}</p>}
            <p className="hint">PDF 含制作模板、步骤说明、描剪底样三页，附 50 mm 校验条与图例。打印请选 100%，关闭"适应页面"。</p>
          </section>

          {selected && (
            <section>
              <h2>选中剪口</h2>
              <p className="hint">类型：{kindLabel(selected)}</p>
              <button type="button" className="danger" onClick={() => dispatch({ type: 'removeCut', id: selected.id })}>
                删除此剪口
              </button>
            </section>
          )}
        </aside>
      </main>

      {pendingFold && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <h3>切换折法</h3>
            <p>
              切换到「{getFold(pendingFold).name}」时，现有 {project.cuts.length} 个剪口怎么处理？
            </p>
            <div className="modal-actions">
              <button type="button" className="primary" onClick={() => confirmFold(true)}>
                保留剪口（超出新边界的会被裁掉）
              </button>
              <button type="button" onClick={() => confirmFold(false)}>
                清空并新建
              </button>
              <button type="button" className="ghost" onClick={() => setPendingFold(null)}>
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {state.notice && <div className={`toast ${state.notice.kind}`}>{state.notice.text}</div>}
    </div>
  );
}

function filename(ext: string): string {
  const d = new Date();
  const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}-${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`;
  return `窗花剪纸-${stamp}.${ext}`;
}

function kindLabel(cut: CutShape): string {
  switch (cut.kind) {
    case 'circle':
      return '圆形';
    case 'triangle':
      return '三角形';
    case 'rect':
      return '矩形';
    case 'freehand':
      return '自由绘制';
    default:
      return '多边形';
  }
}
